import type { ISaveAdapter, ISaveData } from "../types";

/**
 * Cross-platform save manager. Adapter pattern.
 * Adapters: LocalStorage (browser MVP), Electron (future), Capacitor (future), Memory (fallback).
 */
export class SaveManager {
  private readonly SAVE_VERSION = 1;
  private pending = new Map<string, string>();
  private flushTimer: number | null = null;

  constructor(private readonly adapter: ISaveAdapter) {}

  async read<T = Record<string, unknown>>(key: string): Promise<T | null> {
    const raw = await this.adapter.read(key);
    if (!raw) return null;
    try {
      const data = JSON.parse(raw) as ISaveData;
      if (data.version > this.SAVE_VERSION) {
        console.warn(`[save] key "${key}" version ${data.version} > current ${this.SAVE_VERSION}; refusing to load`);
        return null;
      }
      return this.migrateIfNeeded(data).payload as T;
    } catch {
      const backupKey = `${key}.corrupt.${Date.now()}`;
      try { await this.adapter.write(backupKey, raw); } catch { /* best-effort */ }
      console.warn(`[save] key "${key}" corrupt; backed up to "${backupKey}"`);
      return null;
    }
  }

  async write<T = Record<string, unknown>>(key: string, payload: T): Promise<void> {
    const data: ISaveData = {
      version: this.SAVE_VERSION,
      timestamp: Date.now(),
      platform: this.adapter.platform,
      payload: payload as Record<string, unknown>
    };
    const serialized = JSON.stringify(data);
    this.pending.set(key, serialized);
    this.scheduleFlush();
  }

  async flush(): Promise<void> {
    if (this.pending.size === 0) return;
    const entries = Array.from(this.pending.entries());
    this.pending.clear();
    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    await Promise.all(entries.map(([k, v]) => this.adapter.write(k, v)));
  }

  async clear(key: string): Promise<void> {
    this.pending.delete(key);
    await this.adapter.clear(key);
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, 250);
  }

  private migrateIfNeeded(data: ISaveData): ISaveData {
    if (data.version === this.SAVE_VERSION) return data;
    return { ...data, version: this.SAVE_VERSION };
  }
}
